import { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../config/api";
import "./invoiceDashboard.css";

export default function InvoiceDashboard({ onSelect }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [editForm, setEditForm] = useState({
    clientName: "",
    company: "",
    email: "",
    phone: "",
    description: "",
    total: "",
    paymentStatus: "unpaid"
  });

  const formatCurrency = (v) =>
    `R ${Number(v || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
    })}`;

  const formatDate = (dateValue) => {
    if (!dateValue) return "Unknown date";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "Unknown date";
    return date.toLocaleDateString();
  };

  const fetchJobs = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError("");
      const res = await fetch(`${API_BASE_URL}/api/jobs`, { signal });

      let data = [];
      try {
        data = await res.json();
      } catch {
        data = [];
      }

      if (!res.ok) {
        throw new Error(data?.error || "Unable to load invoices.");
      }

      const normalized = Array.isArray(data) ? data : [];
      normalized.sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0));
      setJobs(normalized);
      setSelectedJob((prev) => {
        if (!prev?._id) return prev;
        return normalized.find((j) => j._id === prev._id) || null;
      });
    } catch (err) {
      if (err?.name === "AbortError") return;
      setError(err?.message || "Unable to load invoices.");
      setJobs([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  const handleDelete = async (id) => {
    if (!id) {
      setError("This invoice cannot be deleted because its ID is missing.");
      return;
    }

    const confirmed = window.confirm("Delete this invoice permanently?");
    if (!confirmed) return;

    try {
      setDeletingId(id);
      setError("");

      const res = await fetch(`${API_BASE_URL}/api/jobs/${id}`, {
        method: "DELETE"
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (!res.ok) {
        throw new Error(data?.error || "Failed to delete invoice.");
      }

      setJobs((prev) => prev.filter((job) => job._id !== id));
      if (selectedJob?._id === id) {
        setSelectedJob(null);
      }
    } catch (err) {
      setError(err?.message || "Failed to delete invoice.");
    } finally {
      setDeletingId(null);
    }
  };

  const openEditor = (job) => {
    setSelectedJob(job);
    setEditForm({
      clientName: job?.client?.name || "",
      company: job?.client?.company || "",
      email: job?.client?.email || job?.client?.contact || "",
      phone: job?.client?.phone || "",
      description: job?.description || "",
      total: String(Number(job?.total || 0))
      ,
      paymentStatus: job?.paymentStatus || "unpaid"
    });
    onSelect?.(job);
  };

  const handleSaveChanges = async () => {
    if (!selectedJob?._id) return;
    try {
      setSaving(true);
      setError("");

      const payload = {
        ...selectedJob,
        description: editForm.description,
        total: Number(editForm.total) || 0,
        paymentStatus: editForm.paymentStatus === "paid" ? "paid" : "unpaid",
        paidAt: editForm.paymentStatus === "paid" ? (selectedJob?.paidAt || new Date().toISOString()) : null,
        client: {
          ...(selectedJob.client || {}),
          name: editForm.clientName,
          company: editForm.company,
          email: editForm.email,
          phone: editForm.phone,
          contact: editForm.email || editForm.phone
        }
      };

      const res = await fetch(`${API_BASE_URL}/api/jobs/${selectedJob._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to update invoice.");

      setSelectedJob(data);
      setJobs((prev) => prev.map((j) => (j._id === data._id ? data : j)));
      onSelect?.(data);
    } catch (err) {
      setError(err?.message || "Failed to update invoice.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchJobs(controller.signal);
    return () => controller.abort();
  }, [fetchJobs]);

  const totalInvoices = jobs.length;
  const totalValue = useMemo(
    () => jobs.reduce((sum, job) => sum + Number(job?.total || job?.summary?.total || 0), 0),
    [jobs]
  );
  const paidRevenue = useMemo(
    () =>
      jobs
        .filter((job) => job?.paymentStatus === "paid")
        .reduce((sum, job) => sum + Number(job?.total || job?.summary?.total || 0), 0),
    [jobs]
  );
  const paidCount = useMemo(
    () => jobs.filter((job) => job?.paymentStatus === "paid").length,
    [jobs]
  );

  return (
    <div className="invoice-dashboard">

      <div className="summary-header">
        <div>
          <h2>Saved Invoices</h2>
          <p className="subtitle">
            Browse, review and manage generated quotations ({totalInvoices} invoices |{" "}
            {formatCurrency(totalValue)})
          </p>
        </div>
        <button className="download-btn" onClick={() => fetchJobs()} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>
      <div className="admin-kpi-row">
        <div className="admin-kpi">
          <span>Total Invoices</span>
          <strong>{totalInvoices}</strong>
        </div>
        <div className="admin-kpi">
          <span>Total Value</span>
          <strong>{formatCurrency(totalValue)}</strong>
        </div>
        <div className="admin-kpi">
          <span>Paid Revenue</span>
          <strong>{formatCurrency(paidRevenue)}</strong>
        </div>
        <div className="admin-kpi">
          <span>Paid Invoices</span>
          <strong>{paidCount}</strong>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {error && (
        <button className="btn-secondary" onClick={() => fetchJobs()} disabled={loading}>
          Retry Loading
        </button>
      )}
      {!onSelect && <p className="muted">Select handler not connected in this view.</p>}
      {loading && <p className="muted">Loading invoices...</p>}
      {!loading && jobs.length === 0 && <p className="empty">No invoices yet</p>}

      <div className="invoice-admin-grid">
        <div>
          {jobs.map((job, index) => (
            <div key={job._id || `${job.invoiceNumber || "invoice"}-${index}`} className="job-card">

              <div>
                <strong>{job.invoiceNumber || "Invoice"}</strong>
                <p>{job.route?.from || "-"} → {job.route?.to || "-"}</p>
                <p className="muted">
                  {formatDate(job?.date)} | {formatCurrency(job?.total || job?.summary?.total)}
                </p>
                <p className={`payment-pill ${job?.paymentStatus === "paid" ? "paid" : "unpaid"}`}>
                  {job?.paymentStatus === "paid" ? "Paid" : "Unpaid"}
                </p>
              </div>

              <div className="job-actions">
                <button onClick={() => openEditor(job)} className="btn-secondary">Edit</button>
                <button onClick={() => handleDelete(job._id)} disabled={deletingId === job._id}>
                  {deletingId === job._id ? "Deleting..." : "Delete"}
                </button>
              </div>

            </div>
          ))}
        </div>

        <div className="invoice-editor">
          <h3>Edit Selected Invoice</h3>
          {!selectedJob && <p className="muted">Select an invoice to edit.</p>}
          {selectedJob && (
            <>
              <div className="editor-grid">
                <div className="editor-field">
                  <label htmlFor="admin-client-name">Client Name</label>
                  <input
                    id="admin-client-name"
                    placeholder="Client Name"
                    value={editForm.clientName}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, clientName: e.target.value }))}
                  />
                </div>
                <div className="editor-field">
                  <label htmlFor="admin-company">Company</label>
                  <input
                    id="admin-company"
                    placeholder="Company"
                    value={editForm.company}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, company: e.target.value }))}
                  />
                </div>
                <div className="editor-field">
                  <label htmlFor="admin-email">Email</label>
                  <input
                    id="admin-email"
                    placeholder="Email"
                    value={editForm.email}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div className="editor-field">
                  <label htmlFor="admin-phone">Phone</label>
                  <input
                    id="admin-phone"
                    placeholder="Phone"
                    value={editForm.phone}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div className="editor-field">
                  <label htmlFor="admin-total">Total</label>
                  <input
                    id="admin-total"
                    placeholder="Total"
                    type="number"
                    step="any"
                    value={editForm.total}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, total: e.target.value }))}
                  />
                </div>
                <div className="editor-field">
                  <label htmlFor="admin-payment-status">Payment Status</label>
                  <select
                    id="admin-payment-status"
                    value={editForm.paymentStatus}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, paymentStatus: e.target.value }))}
                  >
                    <option value="unpaid">Unpaid</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>
              <label htmlFor="admin-description">Service Description</label>
              <textarea
                id="admin-description"
                placeholder="Service description"
                value={editForm.description}
                onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
              />
              <button className="download-btn" onClick={handleSaveChanges} disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </>
          )}
        </div>
      </div>

    </div>
  );
}